<template lang="html">
  <!-- Omitted when the All feed has no News/Article items -->
  <section
    v-if="hasAnyNotes"
    class="home-content-notes mx-auto w-full max-w-(--ui-container) px-3 py-6 sm:px-6 sm:py-8"
    aria-labelledby="home-content-notes-heading"
  >
    <header class="mb-4 flex flex-col gap-4 sm:mb-5 sm:flex-row sm:items-end sm:justify-between">
      <h2
        id="home-content-notes-heading"
        class="text-2xl font-bold tracking-tight text-highlighted sm:text-3xl"
      >
        Новости и блог
      </h2>

      <UTabs
        v-model="activeTab"
        :items="tabItems"
        :content="false"
        color="primary"
        variant="link"
        size="sm"
        class="w-full sm:w-auto"
        :ui="{ list: 'justify-start sm:justify-end' }"
      />
    </header>

    <div
      v-if="pending"
      class="flex justify-center py-10"
      aria-busy="true"
      aria-live="polite"
    >
      <AppLoader class="h-12 w-12" />
    </div>

    <p
      v-else-if="!items.length"
      class="text-sm text-muted"
    >
      {{ emptyText }}
    </p>

    <div
      v-else
      class="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3 xl:grid-cols-4"
    >
      <ContentCard
        v-for="item in items"
        :key="`${item.type}-${item.slug}`"
        :value="item"
        :base-path="resolveBasePath(item)"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
/**
 * Home “notes” strip: recent CMS content with DNS-style type tabs.
 * Uses a fixed Limit list (no infinite scroll). Section is gated on the All feed.
 */
import type { TabsItem } from '@nuxt/ui'
import {
  ContentType,
  ShowcaseContentListItemDto,
  SortDirection,
  type ContentListRequestQuery,
} from '@repo/api/web/models'

import ContentCard from '../Card/ContentCard.vue'

import AppLoader from '~/app/components/Loader/AppLoader.vue'

type NotesTab = 'all' | 'news' | 'blog'

const LIMIT = 8

const TAB_META: Record<
  NotesTab,
  { label: string, type?: ContentType, basePath?: string, emptyText: string }
> = {
  all: {
    label: 'Все',
    emptyText: 'Материалов пока нет.',
  },
  news: {
    label: 'Новости',
    type: ContentType.NEWS,
    basePath: '/news',
    emptyText: 'Новостей пока нет.',
  },
  blog: {
    label: 'Блоги',
    type: ContentType.ARTICLE,
    basePath: '/blog',
    emptyText: 'Статей пока нет.',
  },
}

const { service } = useContent()

const activeTab = ref<NotesTab>('all')

const tabItems = computed<TabsItem[]>(() =>
  (Object.keys(TAB_META) as NotesTab[]).map(value => ({
    label: TAB_META[value].label,
    value,
  })),
)

function contentBasePath(type: ContentType): string {
  switch (type) {
    case ContentType.NEWS:
      return '/news'
    case ContentType.ARTICLE:
      return '/blog'
    default:
      return '/pages'
  }
}

function resolveBasePath(item: ShowcaseContentListItemDto): string {
  const tabPath = TAB_META[activeTab.value].basePath
  return tabPath ?? contentBasePath(item.type)
}

function buildQuery(type?: ContentType): ContentListRequestQuery {
  const query: ContentListRequestQuery = {
    Limit: LIMIT,
    SortField: 'publishAt',
    SortDirection: SortDirection.DESC,
  }
  if (type) query['Filter.Type'] = type
  return query
}

/** All tab omits Filter.Type; drop Page/Instruction so legal/static pages stay out of the strip. */
function toFeedItems(
  raw: ShowcaseContentListItemDto[] | undefined,
  tab: NotesTab,
): ShowcaseContentListItemDto[] {
  const list = ShowcaseContentListItemDto.createFromCollection(raw ?? [])
  if (tab !== 'all') return list
  return list.filter(
    item => item.type === ContentType.NEWS || item.type === ContentType.ARTICLE,
  )
}

async function fetchTab(tab: NotesTab): Promise<ShowcaseContentListItemDto[]> {
  // All: oversample then drop Page/Instruction so mixed feed still fills ~LIMIT cards
  const limit = tab === 'all' ? LIMIT * 3 : LIMIT
  const page = await service.list({
    ...buildQuery(TAB_META[tab].type),
    Limit: limit,
  })
  return toFeedItems(page.items, tab).slice(0, LIMIT)
}

const { data, pending } = await useAsyncData(
  'home-content-notes',
  () => fetchTab(activeTab.value),
  { watch: [activeTab] },
)

const items = computed(() => data.value ?? [])

/** Gate from the initial All load (activeTab starts as `all`). */
const hasAnyNotes = ref(items.value.length > 0)

const emptyText = computed(() => TAB_META[activeTab.value].emptyText)
</script>
